import "../../styles/field.css";
import "./index.css";
import { clsx } from "clsx";
import type { FactoryOpts, InputMask } from "imask";
import { XIcon } from "lucide-react";
import type { ChangeEvent, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useIMask } from "react-imask";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useTranslations } from "../../i18n";
import {
  composeEventHandlers,
  mergeRefs,
  textFieldButtonSize,
} from "../../utils";
import { IconButton } from "../Button/IconButton";
import { FieldAccessory } from "../FieldAccessory";
import { Spinner } from "../Spinner";
import type { ISpinnerSize } from "../Spinner/types";
import type { IInputProps, IInputSize } from "./types";

export const Input = (props: IInputProps) => {
  const { t } = useTranslations();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: IInputSize = props.size || (isMobile ? "lg" : "md");
  const onChange = props.onChange;
  const regex = props.regex;
  const internalRef = useRef<HTMLInputElement>(null);

  const hasStartAccessory = !!props.startAccessory;
  const hasEndAccessory = !!(
    props.endAccessory ||
    props.onClear ||
    props.loading
  );

  const rootClass: string = clsx(
    "nuco-field nuco-input",
    `nuco-input--${size}`,
    props.focused && "nuco-field--focused",
    props.invalid && "nuco-field--invalid nuco-color-danger",
    props.disabled && "nuco-field--disabled",
    props.readOnly && "nuco-field--readOnly",
    props.className,
  );

  const fontSizeMap: Record<IInputSize, string> = {
    sm: "nuco-text nuco-text--md",
    md: "nuco-text nuco-text--md",
    lg: "nuco-text nuco-text--md",
  };

  const inputClass: string = clsx(
    "nuco-field__control nuco-input__input",
    fontSizeMap[size],
    hasStartAccessory && "nuco-input__input--withStartAccessory",
  );

  const spinnerSizeMap: Record<IInputSize, ISpinnerSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const spinnerSize: ISpinnerSize =
    props.spinnerProps?.size || spinnerSizeMap[size];

  const IMaskOptions = useMemo(() => {
    if (!props.mask) return null;

    if (typeof props.mask === "string") {
      return { mask: props.mask };
    }

    return props.mask;
  }, [props.mask]);

  const handleMaskAccept = useCallback(
    (value: string, mask: InputMask<FactoryOpts>) => {
      if (!IMaskOptions) return;

      let outputValue: string = value;
      if (IMaskOptions.unmask === true) {
        outputValue = mask.unmaskedValue;
      } else if (IMaskOptions.unmask === "typed") {
        outputValue = mask.typedValue;
      }

      onChange?.(outputValue, undefined);
      IMaskOptions.onAccept?.(value, mask);
    },
    [IMaskOptions, onChange],
  );

  const handleMaskComplete = useCallback(
    (value: string, mask: InputMask<FactoryOpts>) => {
      IMaskOptions?.onComplete?.(value, mask);
    },
    [IMaskOptions],
  );

  const {
    ref: maskRef,
    value: maskValue,
    setValue: setMaskValue,
  } = useIMask<HTMLInputElement>(
    IMaskOptions ? IMaskOptions : { mask: "" },
    IMaskOptions
      ? {
          onAccept: handleMaskAccept,
          onComplete: handleMaskComplete,
          ref: props.ref,
        }
      : undefined,
  );

  const inputRef = mergeRefs(internalRef, maskRef, props.ref);

  useEffect(() => {
    if (
      IMaskOptions &&
      props.value !== undefined &&
      props.value !== maskValue
    ) {
      setMaskValue(String(props.value));
    }
  }, [props.value, IMaskOptions, maskValue, setMaskValue]);

  const nativeProps: IInputProps = useMemo(() => {
    const nativeProps = { ...props };
    delete nativeProps.startAccessory;
    delete nativeProps.endAccessory;
    delete nativeProps.onClear;
    delete nativeProps.loading;
    delete nativeProps.invalid;
    delete nativeProps.focused;
    delete nativeProps.spinnerProps;
    delete nativeProps.size;
    delete nativeProps.regex;
    delete nativeProps.onChange;
    delete nativeProps.mask;
    return nativeProps;
  }, [props]);

  const handleClear = useCallback(
    (e: MouseEvent) => {
      if (IMaskOptions) {
        setMaskValue("");
        onChange?.("", undefined);
      }
      props.onClear?.(e);
      internalRef.current?.focus();
    },
    [IMaskOptions, props, setMaskValue, onChange],
  );

  const clearButtonJSX = !props.disabled && props.onClear && (
    <IconButton
      variant="ghost"
      size={textFieldButtonSize[size]}
      color="neutral"
      onClick={handleClear}
      content={<XIcon />}
      aria-label={t("ui.input.clearField")}
    />
  );

  const spinnerJSX = !props.disabled && props.loading && (
    <FieldAccessory size={size}>
      <Spinner color="neutral" size={spinnerSize} {...props.spinnerProps} />
    </FieldAccessory>
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (IMaskOptions) return;

      let value = e.currentTarget.value;

      if (regex) {
        value = value
          .split("")
          .filter((char) => regex.test(char))
          .join("");
      }

      if (onChange) {
        onChange(value, e);
      }
    },
    [onChange, regex, IMaskOptions],
  );

  const value: string = useMemo(() => {
    return IMaskOptions ? maskValue : props.value?.toString() || "";
  }, [props.value, IMaskOptions, maskValue]);

  const internalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!regex) return;

      const isValidCharacter = regex.test(e.key);
      const navigationKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
        "Enter",
      ];

      if (!isValidCharacter && !navigationKeys.includes(e.key)) {
        e.preventDefault();
      }
    },
    [regex],
  );

  const onKeyDownComposed = useMemo(
    () => composeEventHandlers(props.onKeyDown as any, internalKeyDown as any),
    [props.onKeyDown, internalKeyDown],
  );

  return (
    <div className={rootClass}>
      {hasStartAccessory && (
        <div className="nuco-input__accessory nuco-input__startAccessory">
          {props.startAccessory}
        </div>
      )}
      <input
        {...nativeProps}
        ref={inputRef}
        size={undefined}
        className={inputClass}
        onChange={handleChange}
        onKeyDown={onKeyDownComposed}
        value={IMaskOptions ? undefined : props.value}
        aria-invalid={props.invalid}
      />
      {hasEndAccessory && (
        <div className="nuco-input__accessory nuco-input__endAccessory">
          {value.length > 0 && clearButtonJSX}
          {!spinnerJSX && props.endAccessory && props.endAccessory}
          {spinnerJSX}
        </div>
      )}
    </div>
  );
};
